import { createContext, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';

/**
 * Sistema de design da gestão (docs/14 §4): controlos de 44 px, rótulo por
 * cima, ajuda por baixo, o erro no lugar da ajuda; números à direita com
 * vírgula; secções com título; largura de cada campo conforme o conteúdo.
 */

export function Button({ variant = 'primary', size = 'md', icon: Icon, className, children, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger' | 'subtle'; size?: 'sm' | 'md' | 'lg'; icon?: LucideIcon;
}) {
  return (
    <button
      {...p}
      className={clsx('inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-semibold transition-colors disabled:pointer-events-none disabled:opacity-40', {
        'h-9 px-3 text-sm': size === 'sm', 'h-11 px-4': size === 'md', 'h-12 px-5 text-base': size === 'lg',
        'bg-brand-700 text-white hover:bg-brand-800': variant === 'primary',
        'border border-base bg-[var(--surface)] hover:bg-surface-2': variant === 'ghost',
        'border border-brand-700 text-brand-700 hover:bg-brand-50 dark:hover:bg-brand-700/10': variant === 'danger',
        'text-muted hover:bg-surface-2 hover:text-[var(--text)]': variant === 'subtle',
      }, className)}
    >{Icon && <Icon size={size === 'sm' ? 16 : 18} />}{children}</button>
  );
}

/** Larguras por tipo de conteúdo (docs/14 §4.3). */
export const W = { money: 'w-40', qty: 'w-30', code: 'w-50', nuit: 'w-40', phone: 'w-45', date: 'w-45', full: 'w-full' } as const;

export function Field({ label, hint, error, required, children, className }: { label: string; hint?: string; error?: string | null; required?: boolean; children: ReactNode; className?: string }) {
  return (
    <label className={clsx('block min-w-0', className)}>
      <span className="text-sm font-medium">{label}{required && <span aria-hidden className="ml-0.5 text-brand-700">*</span>}</span>
      <div className="mt-1.5">{children}</div>
      {error
        ? <span role="alert" className="mt-1 flex items-center gap-1 text-xs text-brand-700"><AlertCircle size={13} />{error}</span>
        : hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  );
}

const control = 'h-11 w-full rounded-lg border border-base bg-[var(--surface)] px-3 outline-none transition placeholder:text-muted/70 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 disabled:bg-surface-2 disabled:text-muted';

export const Input = ({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) => <input {...p} className={clsx(control, className)} />;

export const Select = ({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => <select {...p} className={clsx(control, 'pr-8', className)} />;

export const Textarea = ({ className, ...p }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...p} className={clsx(control, 'h-auto min-h-24 py-2.5', className)} />
);

/** Valor com sufixo fora do campo (MT, kg, min…): algarismos tabulares, à direita. */
export function NumberInput({ suffix, className, ...p }: InputHTMLAttributes<HTMLInputElement> & { suffix?: string }) {
  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <input inputMode="decimal" autoComplete="off" {...p} className={clsx(control, 'num text-right')} />
      {suffix && <span className="shrink-0 text-sm font-medium text-muted">{suffix}</span>}
    </div>
  );
}
export const MoneyInput = (p: InputHTMLAttributes<HTMLInputElement>) => <NumberInput suffix="MT" placeholder="0,00" className={W.money} {...p} />;

/** NUIT: só algarismos, 9. */
export function NuitInput({ value, onChange, ...p }: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & { value: string; onChange: (v: string) => void }) {
  return <input {...p} inputMode="numeric" maxLength={9} placeholder="9 algarismos" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 9))} className={clsx(control, 'num tracking-wider', W.nuit)} />;
}

/** Telefone moçambicano: mostra +258 84 123 4567 enquanto se escreve. */
export function PhoneInput({ value, onChange, ...p }: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & { value: string; onChange: (v: string) => void }) {
  return <input {...p} type="tel" inputMode="tel" autoComplete="tel" placeholder="+258 84 123 4567" value={value} onChange={(e) => onChange(formatPhone(e.target.value))} className={clsx(control, 'num', W.phone)} />;
}
export function formatPhone(raw: string): string {
  let d = raw.replace(/[^\d+]/g, '');
  if (/^8\d/.test(d)) d = `+258${d}`;
  const m = /^\+258(\d{0,2})(\d{0,3})(\d{0,4})/.exec(d);
  return m ? ['+258', m[1], m[2], m[3]].filter(Boolean).join(' ') : d;
}

/** Interruptor com título e descrição (módulos, opções). */
export function Toggle({ checked, onChange, label, description, disabled, badge }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode; disabled?: boolean; badge?: ReactNode }) {
  return (
    <label className={clsx('flex cursor-pointer items-start gap-3 rounded-xl border border-base p-3.5 transition-colors', checked ? 'bg-brand-50/50 dark:bg-white/5' : 'hover:bg-surface-2', disabled && 'cursor-not-allowed opacity-60')}>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">{label}{badge}</span>
        {description && <span className="mt-0.5 block text-xs text-muted">{description}</span>}
      </span>
      <input type="checkbox" role="switch" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span aria-hidden className={clsx('relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors peer-focus-visible:ring-4 peer-focus-visible:ring-brand-500/30', checked ? 'bg-brand-700' : 'bg-[var(--border)]')}>
        <span className={clsx('absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all', checked ? 'left-5.5' : 'left-0.5')} />
      </span>
    </label>
  );
}

/** Escolha entre 2 a 4 opções curtas (Produto/Serviço, Dia/Semana). */
export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: readonly { value: T; label: string; icon?: LucideIcon }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-surface-2 p-1">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)}
          className={clsx('flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition', value === o.value ? 'bg-[var(--surface)] shadow-sm' : 'text-muted hover:text-[var(--text)]')}>
          {o.icon && <o.icon size={15} />}{o.label}
        </button>
      ))}
    </div>
  );
}

/** Secção de formulário: título, descrição e grelha de 1/2/3 colunas. */
export function Section({ title, description, children, cols = 2, aside }: { title: string; description?: string; children: ReactNode; cols?: 1 | 2 | 3; aside?: ReactNode }) {
  return (
    <section className="border-t border-base pt-6 first:border-t-0 first:pt-0">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold">{title}</h3>
          {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
        </div>
        {aside}
      </div>
      <div className={clsx('grid gap-x-4 gap-y-4', { 'sm:grid-cols-2': cols >= 2, 'xl:grid-cols-3': cols === 3 })}>{children}</div>
    </section>
  );
}

export function Card({ title, description, actions, children, padded = true }: { title?: string; description?: string; actions?: ReactNode; children: ReactNode; padded?: boolean }) {
  return (
    <section className="surface rounded-2xl shadow-xs">
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-base px-4 py-3.5 xl:px-6">
          <div>
            {title && <h2 className="font-display text-lg font-semibold">{title}</h2>}
            {description && <p className="text-sm text-muted">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className={padded ? 'p-4 xl:p-6' : ''}>{children}</div>
    </section>
  );
}

/** Barra de acções fixa no fundo de um formulário longo. */
export function FormActions({ children }: { children: ReactNode }) {
  return <div className="sticky bottom-0 z-10 -mx-4 -mb-4 mt-6 flex flex-wrap gap-2 border-t border-base bg-[var(--surface)]/95 px-4 py-3 backdrop-blur xl:-mx-6 xl:-mb-6 xl:px-6">{children}</div>;
}

export function Alert({ tone = 'error', children }: { tone?: 'error' | 'info' | 'ok' | 'warn'; children: ReactNode }) {
  const Icon = { error: AlertCircle, info: Info, ok: CheckCircle2, warn: AlertCircle }[tone];
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={clsx('flex gap-2.5 rounded-xl p-3.5 text-sm', {
      'bg-brand-50 text-brand-800 dark:bg-brand-700/15 dark:text-brand-100': tone === 'error',
      'bg-sky-50 text-sky-900 dark:bg-sky-950 dark:text-sky-100': tone === 'info',
      'bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100': tone === 'ok',
      'bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100': tone === 'warn',
    })}><Icon size={18} className="mt-px shrink-0" /><div className="min-w-0 flex-1">{children}</div></div>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'brand' | 'ok' | 'warn' | 'info'; children: ReactNode }) {
  return (
    <span className={clsx('inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold', {
      'bg-surface-2 text-muted': tone === 'neutral', 'bg-brand-50 text-brand-800 dark:bg-brand-700/20 dark:text-brand-100': tone === 'brand',
      'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200': tone === 'ok', 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100': tone === 'warn',
      'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200': tone === 'info',
    })}>{children}</span>
  );
}

/** Estado vazio: ícone, uma frase, e o botão que resolve. */
export function EmptyState({ icon: Icon, title, text, action }: { icon: LucideIcon; title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-surface-2 text-muted"><Icon size={26} strokeWidth={1.6} /></span>
      <p className="mt-4 font-display font-semibold">{title}</p>
      {text && <p className="mt-1 max-w-sm text-sm text-muted">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Esqueleto enquanto carrega (com a forma do conteúdo, em vez de "A carregar…"). */
export function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-label="A carregar" className="space-y-3">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="h-11 animate-pulse rounded-lg bg-surface-2" style={{ width: `${92 - (i % 3) * 14}%` }} />)}
    </div>
  );
}

/** Diálogo modal com tamanhos fixos; no telemóvel ocupa o ecrã. */
export function Dialog({ title, onClose, size = 'md', children, footer }: { title: string; onClose: () => void; size?: 'sm' | 'md' | 'lg'; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog ref={ref} onCancel={(e) => { e.preventDefault(); onClose(); }}
      className={clsx('m-0 h-full max-h-none w-full max-w-none bg-[var(--surface)] p-0 text-[var(--text)] sm:m-auto sm:h-auto sm:max-h-[90vh] sm:rounded-2xl', { 'sm:w-[420px]': size === 'sm', 'sm:w-[560px]': size === 'md', 'sm:w-[760px]': size === 'lg' })}>
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-base px-5 py-3.5">
          <h2 className="font-display text-lg font-semibold">{title}</h2>
          <button type="button" aria-label="Fechar" onClick={onClose} className="grid h-10 w-10 place-items-center rounded-lg text-muted hover:bg-surface-2"><X size={20} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-base px-5 py-3.5">{footer}</div>}
      </div>
    </dialog>
  );
}

// ── Avisos curtos ("Gravado") ───────────────────────────────────────────────
type ToastMsg = { id: number; tone: 'ok' | 'error' | 'info'; text: string };
const ToastCtx = createContext<(text: string, tone?: ToastMsg['tone']) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<ToastMsg[]>([]);
  const push = useCallback((text: string, tone: ToastMsg['tone'] = 'ok') => {
    const id = Date.now() + Math.random();
    setList((l) => [...l.slice(-2), { id, tone, text }]);
    setTimeout(() => setList((l) => l.filter((t) => t.id !== id)), tone === 'error' ? 6000 : 3000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-4 top-4 z-50 flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:top-auto sm:items-end">
        {list.map((t) => {
          const Icon = t.tone === 'ok' ? CheckCircle2 : t.tone === 'error' ? AlertCircle : Info;
          return (
            <div key={t.id} role="status" className="pointer-events-auto flex items-center gap-2.5 rounded-xl bg-ink px-4 py-3 text-sm font-medium text-white shadow-lg dark:bg-white dark:text-ink">
              <Icon size={18} className={t.tone === 'ok' ? 'text-green-400 dark:text-green-600' : t.tone === 'error' ? 'text-red-400 dark:text-red-600' : 'text-sky-400'} />{t.text}
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}
